import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Block } from '../classes/Block';
import type { ApiBlock as ApiBlockType } from '../utils/apiTypes';
import { calculateVideoDimensions, VideoDimensions, VideoMode, VideoSettings } from './VideoSettings';
import { t } from '@lingui/core/macro';
import { getApiUrl } from '../utils/api';
import { authFetch } from '../utils/authFetch';
import { auth } from '../firebase';
import { useProject } from '../contexts/ProjectContext';
import { buildBlockCardIndexMap, CardGroup, getBlockCardGroup } from '../utils/cardGroups';
import { CardFramingSettings } from '../utils/cardFraming';
import { toFontUrlMap } from '../utils/fontHelpers';
import { transformBlockToApiFormat } from '../utils/renderPayload';
import { buildSbsPairLookup } from './SBSBlockTabs';
import { isAdminUserEmail } from '../utils/adminAccess';

interface HtmlPreviewProps {
  blocks: Block[];
  actualSelectedBlockIndex: number;
  videoSettings: VideoSettings;
  columnDefs: any[];
  onPreviewFocusedBlock?: (blockIndex: number) => void;
  scrollToBlockCounter?: number;
  cardGroups?: CardGroup[];
  readOnly?: boolean;
  syncFocusToVisibleViewport?: boolean;
  selectedLogoRowIndex?: number | null;
}

export type PreviewMode = 'single' | 'surrounding' | 'full';

type ApiBlock = ApiBlockType;
const FOCUS_SYNC_SUPPRESSION_MS: number = 350;
const FIT_TO_VIEW_SCROLLBAR_GUTTER_STYLE: string =
  '<style id="fintitles-fit-scrollbar-gutter">html,body{scrollbar-gutter:stable;}</style>';
const PREVIEW_SCROLLBAR_STYLE: string =
  '<style id="fintitles-preview-scrollbar">html{scrollbar-color:#7d7d7d #202020;}::-webkit-scrollbar{width:12px;height:12px}::-webkit-scrollbar-track{background:#202020}::-webkit-scrollbar-thumb{background:#7d7d7d;border:3px solid #202020;border-radius:999px}::-webkit-scrollbar-thumb:hover{background:#9a9a9a}</style>';

interface PreviewFocusMessage {
  type?: string;
  blockName?: string;
  blockIndex?: number;
  blockId?: string;
  reason?: 'load' | 'scroll';
}

interface PendingFullRestore {
  blockId: string | null;
  blockIndex: number;
}

interface PreviewStateRef {
  selectedBlock: Block | undefined;
  selectedBlockIndex: number;
  effectivePreviewMode: PreviewMode;
  selectedLogoRowIndex: number | null;
}

interface CardNavigationStateRef {
  blocks: Block[];
  cardGroups: CardGroup[];
  selectedBlockIndex: number;
  enabled: boolean;
  onPreviewFocusedBlock?: (blockIndex: number) => void;
}

interface ApiPayload {
  video_settings: {
    background_color: string;
    fps_num: number;
    resolution: string;
    transparent: boolean;
    video_time: number;
    aspect_ratio: string;
    fonts: string | undefined;
    logos: string | undefined;
    mode: VideoMode;
    card_framing?: CardFramingSettings;
  };
  data: ApiBlock[];
}

interface PreviewBuffer {
  html: string;
  renderId: number;
}

type PreviewBuffers = [PreviewBuffer, PreviewBuffer];
type PreviewBufferIndex = 0 | 1;
const MIN_PREVIEW_ZOOM: number = 0.01;
const MAX_PREVIEW_ZOOM: number = 4;
const PREVIEW_GRID_CELL_SIZE: number = 20;
const MIN_GRID_SCREEN_SPACING: number = 40;
const DEFAULT_PREVIEW_ZOOM: number = 1;
const PREVIEW_ZOOM_SNAP_TOLERANCE: number = 0.15;

export function snapPreviewZoom(value: number): number {
  return Math.abs(value - DEFAULT_PREVIEW_ZOOM) <= PREVIEW_ZOOM_SNAP_TOLERANCE + Number.EPSILON
    ? DEFAULT_PREVIEW_ZOOM
    : value;
}

export function getPreviewGridSpacing(cellSize: number, scale: number): number {
  if (scale <= 0) return cellSize;
  // Powers of two preserve canvas alignment for both coarser lines and subdivisions.
  const stride = 2 ** Math.ceil(Math.log2(MIN_GRID_SCREEN_SPACING / (cellSize * scale)));
  return cellSize * stride;
}

export const SELECTED_PREVIEW_BLOCK_CLASS: string = 'preview-block-selected';

export interface PreviewBlockMarkerIdentity {
  blockId?: string | null;
  blockIndex?: number | null;
}

export function findPreviewBlockMarker(
  doc: Document,
  identity: PreviewBlockMarkerIdentity
): HTMLElement | null {
  if (identity.blockId) {
    const markerById: HTMLElement | null = doc.querySelector<HTMLElement>(
      `[data-block-index][data-block-id="${CSS.escape(identity.blockId)}"]`
    );
    if (markerById) return markerById;
  }

  if (typeof identity.blockIndex !== 'number' || identity.blockIndex < 0) return null;
  return doc.querySelector<HTMLElement>(`[data-block-index="${identity.blockIndex}"]`);
}

export function clearSelectedPreviewBlock(doc: Document): void {
  doc.querySelectorAll<HTMLElement>(`.${SELECTED_PREVIEW_BLOCK_CLASS}`).forEach((marker: HTMLElement) => {
    marker.classList.remove(SELECTED_PREVIEW_BLOCK_CLASS);
  });
}

export function activateSelectedPreviewBlock(
  doc: Document,
  identity: PreviewBlockMarkerIdentity,
  logoRowIndex: number | null = null
): HTMLElement | null {
  clearSelectedPreviewBlock(doc);
  const selectedMarker: HTMLElement | null = findPreviewBlockMarker(doc, identity);
  if (logoRowIndex !== null && logoRowIndex >= 0) {
    const logoItem: HTMLElement | null = selectedMarker?.querySelector<HTMLElement>(
      `.logo-item[data-logo-row-index="${logoRowIndex}"]`
    ) ?? null;
    logoItem?.classList.add(SELECTED_PREVIEW_BLOCK_CLASS);
    return logoItem;
  }

  selectedMarker?.classList.add(SELECTED_PREVIEW_BLOCK_CLASS);
  return selectedMarker;
}

function createEmptyPreviewBuffers(): PreviewBuffers {
  return [
    { html: '', renderId: 0 },
    { html: '', renderId: 0 },
  ];
}

function getBlocksForSelectedCard(blocks: Block[], cardGroups: CardGroup[], selectedIndex: number): Block[] {
  if (selectedIndex < 0 || selectedIndex >= blocks.length) return [];
  const selectedBlockId: string = blocks[selectedIndex].id;
  const group: CardGroup | undefined = getBlockCardGroup(cardGroups, selectedBlockId);
  if (!group) return [blocks[selectedIndex]];
  return blocks.filter((block: Block) => group.blockIds.includes(block.id) && (!block.isHidden || block.id === selectedBlockId));
}

export function getAdjacentCardBlockIndex(
  blocks: Block[],
  cardGroups: CardGroup[],
  selectedIndex: number,
  direction: -1 | 1
): number | null {
  const selectedBlock: Block | undefined = blocks[selectedIndex];
  if (!selectedBlock) return null;

  const currentCardIndex: number | undefined = buildBlockCardIndexMap(cardGroups).get(selectedBlock.id);
  if (currentCardIndex === undefined) return null;

  const adjacentCard: CardGroup | undefined = cardGroups[currentCardIndex + direction];
  if (!adjacentCard) return null;

  const adjacentBlockId: string | undefined = adjacentCard.blockIds[0];
  const adjacentBlockIndex: number = blocks.findIndex((block: Block) => block.id === adjacentBlockId);
  return adjacentBlockIndex === -1 ? null : adjacentBlockIndex;
}

function getSafeFileNameSegment(value: string | null | undefined, fallback: string): string {
  const safeValue: string = (value || fallback)
    .trim()
    .replace(/[^a-z0-9._-]+/gi, '_')
    .replace(/^_+|_+$/g, '');

  return safeValue || fallback;
}

export function getBlocksForPreviewScope(
  blocks: Block[],
  cardGroups: CardGroup[],
  selectedIndex: number,
  previewMode: PreviewMode,
  videoMode: VideoMode
): Block[] {
  if (!blocks?.length) return [];

  if (previewMode === 'full' && videoMode !== 'cards') {
    return blocks.filter((block: Block, index: number) => !block.isHidden || index === selectedIndex);
  }

  if (selectedIndex < 0 || selectedIndex >= blocks.length) return [];

  if (videoMode === 'cards') {
    return getBlocksForSelectedCard(blocks, cardGroups, selectedIndex);
  }

  const sbsPairLookup = buildSbsPairLookup(blocks);
  const selectedPair = sbsPairLookup.byIndex.get(selectedIndex)?.pair;
  const selectedStartIndex: number = selectedPair?.startIndex ?? selectedIndex;
  const selectedEndIndex: number = selectedPair?.endIndex ?? selectedIndex;
  const previewBlockIndexes: Set<number> = new Set<number>();

  const addBlockUnit = (blockIndex: number): void => {
    if (blockIndex < 0 || blockIndex >= blocks.length) return;

    const pair = sbsPairLookup.byIndex.get(blockIndex)?.pair;
    const startIndex: number = pair?.startIndex ?? blockIndex;
    const endIndex: number = pair?.endIndex ?? blockIndex;

    for (let index = startIndex; index <= endIndex; index += 1) {
      if (!blocks[index].isHidden || index === selectedIndex) {
        previewBlockIndexes.add(index);
      }
    }
  };

  addBlockUnit(selectedIndex);

  if (previewMode === 'surrounding') {
    addBlockUnit(selectedStartIndex - 1);
    addBlockUnit(selectedEndIndex + 1);
  }

  return Array.from(previewBlockIndexes)
    .sort((leftIndex: number, rightIndex: number) => leftIndex - rightIndex)
    .map((blockIndex: number) => blocks[blockIndex]);
}

function addFitToViewScrollbarGutter(html: string): string {
  if (!html || html.includes('id="fintitles-fit-scrollbar-gutter"')) {
    return html;
  }

  return html.replace(/<\/head>/i, `${FIT_TO_VIEW_SCROLLBAR_GUTTER_STYLE}</head>`);
}

function addPreviewScrollbarStyling(html: string): string {
  if (!html || html.includes('id="fintitles-preview-scrollbar"')) return html;
  return html.replace(/<\/head>/i, `${PREVIEW_SCROLLBAR_STYLE}</head>`);
}

const HtmlPreview: React.FC<HtmlPreviewProps> = ({
  blocks,
  actualSelectedBlockIndex,
  videoSettings,
  columnDefs,
  onPreviewFocusedBlock,
  scrollToBlockCounter,
  cardGroups = [],
  readOnly = false,
  syncFocusToVisibleViewport = false,
  selectedLogoRowIndex = null,
}) => {
  const { selectedProject } = useProject();
  const selectedProjectId: string | undefined = selectedProject?.id;
  const [previewBuffers, setPreviewBuffers] = useState<PreviewBuffers>(createEmptyPreviewBuffers);
  const [activeBufferIndex, setActiveBufferIndex] = useState<PreviewBufferIndex>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const [iframeHeight, setIframeHeight] = useState<number>(0);
  const [fitViewportHeight, setFitViewportHeight] = useState<number>(0);
  const previewContainerRef = useRef<HTMLDivElement | null>(null);
  const savedScrollRef = useRef<number>(0);
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const gridOverlayRef = useRef<HTMLDivElement | null>(null);
  const savedIframeScrollRef = useRef<number>(0);
  const iframeScrollHandlerRef = useRef<((event: Event) => void) | null>(null);
  const iframeScrollWindowRef = useRef<Window | null>(null);
  const iframeCardNavigationCleanupRef = useRef<(() => void) | null>(null);
  const cardNavigationWheelUntilRef = useRef<number>(0);
  const focusSyncSuppressedRef = useRef<boolean>(false);
  const focusSyncTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingRestoreRef = useRef<PendingFullRestore | null>(null);
  const lastScrollToBlockCounterRef = useRef<number>(0);
  const activeBufferIndexRef = useRef<PreviewBufferIndex>(0);
  const pendingRenderIdRef = useRef<number>(0);
  const requestIdRef = useRef<number>(0);
  const previewBurstStartedRef = useRef<number | null>(null);
  const previewRequestContextRef = useRef<{ projectId?: string; mode: VideoMode; scope: string; retryCount: number; columnDefs: any[] } | null>(null);

  const [zoom, setZoomState] = useState<number>(() => {
    const saved: string | null = localStorage.getItem('fintitles_zoom');
    const value = saved !== null ? parseFloat(saved) : DEFAULT_PREVIEW_ZOOM;
    return Number.isFinite(value) ? Math.min(MAX_PREVIEW_ZOOM, Math.max(MIN_PREVIEW_ZOOM, value)) : DEFAULT_PREVIEW_ZOOM;
  });

  const [fitToView, setFitToViewState] = useState<boolean>(() => {
    const saved: string | null = localStorage.getItem('fintitles_fitToView');
    return saved !== null ? saved === 'true' : true;
  });

  const [fitScale, setFitScale] = useState<number>(1);
  const [gridEnabled, setGridEnabled] = useState<boolean>(() => localStorage.getItem('fintitles_previewGrid') === 'true');

  const syncGridScroll = useCallback((previewWindow: Window): void => {
    if (gridOverlayRef.current) {
      gridOverlayRef.current.style.backgroundPosition = `${-previewWindow.scrollX}px ${-previewWindow.scrollY}px`;
    }
  }, []);

  useEffect(() => {
    const previewWindow = iframeRef.current?.contentWindow;
    if (previewWindow) syncGridScroll(previewWindow);
  }, [gridEnabled, activeBufferIndex, syncGridScroll]);

  const setZoom = (value: number): void => {
    setZoomState(value);
    localStorage.setItem('fintitles_zoom', value.toString());
    setFitToViewState(false);
    localStorage.setItem('fintitles_fitToView', 'false');
  };

  const setFitToView = (value: boolean): void => {
    setFitToViewState(value);
    localStorage.setItem('fintitles_fitToView', value ? 'true' : 'false');
  };

  // Cards always preview their selected card; Roller projects always preview
  // the complete roller so navigation and focus stay in one predictable scope.
  const effectivePreviewMode: PreviewMode = videoSettings.mode === 'cards' ? 'single' : 'full';

  const videoDimensions: VideoDimensions = useMemo(() => (
    calculateVideoDimensions({
      resolution: videoSettings?.resolution ?? 'HD',
      aspect_ratio: videoSettings?.aspect_ratio ?? '16:9',
    })
  ), [videoSettings?.aspect_ratio, videoSettings?.resolution]);
  const originalContentHeight: number = videoDimensions.height;

  const selectedBlock: Block | undefined = blocks[actualSelectedBlockIndex];
  const isAdmin: boolean = isAdminUserEmail(auth.currentUser?.email);
  const previewStateRef = useRef<PreviewStateRef>({
    selectedBlock,
    selectedBlockIndex: actualSelectedBlockIndex,
    effectivePreviewMode,
    selectedLogoRowIndex,
  });
  const cardNavigationStateRef = useRef<CardNavigationStateRef>({
    blocks,
    cardGroups,
    selectedBlockIndex: actualSelectedBlockIndex,
    enabled: videoSettings.mode === 'cards' && fitToView,
    onPreviewFocusedBlock,
  });

  useEffect(() => {
    previewStateRef.current = {
      selectedBlock,
      selectedBlockIndex: actualSelectedBlockIndex,
      effectivePreviewMode,
      selectedLogoRowIndex,
    };
  }, [actualSelectedBlockIndex, effectivePreviewMode, selectedBlock, selectedLogoRowIndex]);

  useEffect(() => {
    cardNavigationStateRef.current = {
      blocks,
      cardGroups,
      selectedBlockIndex: actualSelectedBlockIndex,
      enabled: videoSettings.mode === 'cards' && fitToView,
      onPreviewFocusedBlock,
    };
  }, [actualSelectedBlockIndex, blocks, cardGroups, fitToView, onPreviewFocusedBlock, videoSettings.mode]);

  const suppressFocusSync = useCallback((durationMs: number = FOCUS_SYNC_SUPPRESSION_MS): void => {
    if (focusSyncTimerRef.current) {
      clearTimeout(focusSyncTimerRef.current);
    }
    focusSyncSuppressedRef.current = true;
    focusSyncTimerRef.current = setTimeout(() => {
      focusSyncSuppressedRef.current = false;
      focusSyncTimerRef.current = null;
    }, durationMs);
  }, []);

  const getPreviewViewportHeight = useCallback((
    iframeWindow: Window,
    iframe: HTMLIFrameElement
  ): number => {
    const previewContainer = previewContainerRef.current;
    const renderedScale = iframe.getBoundingClientRect().width / videoDimensions.width;

    return syncFocusToVisibleViewport && previewContainer && renderedScale > 0
      ? previewContainer.clientHeight / renderedScale
      : iframeWindow.innerHeight;
  }, [syncFocusToVisibleViewport, videoDimensions.width]);

  const getCenteredPreviewMarkerScrollTop = useCallback((
    iframeWindow: Window,
    iframe: HTMLIFrameElement,
    marker: HTMLElement
  ): number => {
    const currentScrollTop = iframeWindow.scrollY || iframeWindow.document?.documentElement?.scrollTop || 0;
    const markerTop = marker.getBoundingClientRect().top + currentScrollTop;

    return markerTop - (getPreviewViewportHeight(iframeWindow, iframe) - marker.offsetHeight) / 2;
  }, [getPreviewViewportHeight]);

  const captureFullPreviewSelectionSnapshot = useCallback((): void => {
    const currentSelectedBlock: Block | undefined = previewStateRef.current.selectedBlock;
    const currentSelectedBlockIndex: number = previewStateRef.current.selectedBlockIndex;
    const currentEffectivePreviewMode: PreviewMode = previewStateRef.current.effectivePreviewMode;

    if (currentEffectivePreviewMode !== 'full' || !currentSelectedBlock) {
      pendingRestoreRef.current = null;
      return;
    }

    pendingRestoreRef.current = {
      blockId: currentSelectedBlock.id || null,
      blockIndex: currentSelectedBlockIndex,
    };
  }, []);

  const restoreSelectedBlockInFullPreview = useCallback((
    iframeWindow: Window,
    iframeDoc: Document,
    iframe: HTMLIFrameElement
  ): boolean => {
    const currentSelectedBlock: Block | undefined = previewStateRef.current.selectedBlock;
    const currentSelectedBlockIndex: number = previewStateRef.current.selectedBlockIndex;
    const snapshot: PendingFullRestore | null = pendingRestoreRef.current ?? (
      currentSelectedBlock
        ? {
            blockId: currentSelectedBlock.id || null,
            blockIndex: currentSelectedBlockIndex,
          }
        : null
    );
    if (!snapshot) return false;

    const marker: HTMLElement | null = findPreviewBlockMarker(iframeDoc, {
      blockId: snapshot.blockId,
      blockIndex: snapshot.blockIndex,
    });
    pendingRestoreRef.current = null;

    if (!marker) {
      return false;
    }

    const targetScrollTop = getCenteredPreviewMarkerScrollTop(iframeWindow, iframe, marker);

    suppressFocusSync();
    iframeWindow.scrollTo({ top: Math.max(0, targetScrollTop), behavior: 'auto' });
    savedIframeScrollRef.current = iframeWindow.scrollY || iframeWindow.document?.documentElement?.scrollTop || 0;
    return true;
  }, [getCenteredPreviewMarkerScrollTop, suppressFocusSync]);

  const resetPreviewState = useCallback((nextError: string | null): void => {
    pendingRenderIdRef.current += 1;
    activeBufferIndexRef.current = 0;
    setActiveBufferIndex(0);
    setPreviewBuffers(createEmptyPreviewBuffers());
    setLoading(false);
    setError(nextError);
  }, []);

  const queuePreviewHtml = useCallback((nextHtml: string): void => {
    const inactiveBufferIndex: PreviewBufferIndex = activeBufferIndexRef.current === 0 ? 1 : 0;
    const renderId: number = pendingRenderIdRef.current + 1;
    pendingRenderIdRef.current = renderId;

    setPreviewBuffers((buffers: PreviewBuffers): PreviewBuffers => {
      const nextBuffers: PreviewBuffers = [...buffers];
      nextBuffers[inactiveBufferIndex] = { html: nextHtml, renderId };
      return nextBuffers;
    });
  }, []);

  // Detach iframe scroll listener on unmount
  useEffect(() => {
    return () => {
      if (iframeScrollWindowRef.current && iframeScrollHandlerRef.current) {
        iframeScrollWindowRef.current.removeEventListener('scroll', iframeScrollHandlerRef.current);
        iframeScrollHandlerRef.current = null;
        iframeScrollWindowRef.current = null;
      }
      iframeCardNavigationCleanupRef.current?.();
      iframeCardNavigationCleanupRef.current = null;
      if (focusSyncTimerRef.current) {
        clearTimeout(focusSyncTimerRef.current);
        focusSyncTimerRef.current = null;
      }
      focusSyncSuppressedRef.current = false;
    };
  }, []);

  const navigateCard = useCallback((direction: -1 | 1): void => {
    const state = cardNavigationStateRef.current;
    const adjacentBlockIndex = getAdjacentCardBlockIndex(
      state.blocks,
      state.cardGroups,
      state.selectedBlockIndex,
      direction
    );
    if (adjacentBlockIndex !== null) {
      state.onPreviewFocusedBlock?.(adjacentBlockIndex);
    }
  }, []);

  const handleCardWheel = useCallback((deltaY: number, event: { preventDefault: () => void }): void => {
    if (!cardNavigationStateRef.current.enabled || deltaY === 0) return;

    event.preventDefault();
    const now: number = Date.now();
    if (now < cardNavigationWheelUntilRef.current) return;
    cardNavigationWheelUntilRef.current = now + 250;
    navigateCard(deltaY > 0 ? 1 : -1);
  }, [navigateCard]);

  const handleCardKeyDown = useCallback((key: string, event: { preventDefault: () => void }): void => {
    if (!cardNavigationStateRef.current.enabled) return;
    if (key !== 'PageDown' && key !== 'PageUp') return;

    event.preventDefault();
    navigateCard(key === 'PageDown' ? 1 : -1);
  }, [navigateCard]);

  const installCardNavigationListeners = useCallback((iframeWindow: Window): void => {
    iframeCardNavigationCleanupRef.current?.();

    const handleWheel = (event: WheelEvent): void => handleCardWheel(event.deltaY, event);
    const handleKeyDown = (event: KeyboardEvent): void => handleCardKeyDown(event.key, event);

    iframeWindow.addEventListener('wheel', handleWheel, { passive: false });
    iframeWindow.addEventListener('keydown', handleKeyDown);
    iframeCardNavigationCleanupRef.current = () => {
      iframeWindow.removeEventListener('wheel', handleWheel);
      iframeWindow.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleCardKeyDown, handleCardWheel]);

  const getVisiblePreviewBlockIndex = useCallback((iframe: HTMLIFrameElement): number | null => {
    const previewContainer = previewContainerRef.current;
    const iframeDocument = iframe.contentDocument;
    if (!previewContainer || !iframeDocument) return null;

    const iframeRect = iframe.getBoundingClientRect();
    const containerRect = previewContainer.getBoundingClientRect();
    const viewportTop = Math.max(containerRect.top, iframeRect.top);
    const viewportBottom = Math.min(containerRect.bottom, iframeRect.bottom);
    if (viewportBottom <= viewportTop || iframeRect.width <= 0) return null;

    const scale = iframeRect.width / videoDimensions.width;
    const viewportCenter = (viewportTop + viewportBottom) / 2;
    let closestVisibleIndex: number | null = null;
    let closestVisibleDistance = Number.POSITIVE_INFINITY;

    iframeDocument.querySelectorAll<HTMLElement>('[data-block-index]').forEach((element) => {
      const index = Number(element.getAttribute('data-block-index'));
      if (!Number.isInteger(index)) return;

      const blockRect = element.getBoundingClientRect();
      const blockTop = iframeRect.top + blockRect.top * scale;
      const blockBottom = blockTop + blockRect.height * scale;
      if (blockBottom <= viewportTop || blockTop >= viewportBottom) return;

      const distance = Math.abs(((blockTop + blockBottom) / 2) - viewportCenter);
      if (distance < closestVisibleDistance) {
        closestVisibleDistance = distance;
        closestVisibleIndex = index;
      }
    });

    return closestVisibleIndex;
  }, [videoDimensions.width]);

  const getMessageBlockIndex = useCallback((message: PreviewFocusMessage): number => {
    if (typeof message.blockId === 'string' && message.blockId.trim() !== '') {
      return blocks.findIndex((block: Block) => block.id === message.blockId);
    }
    if (typeof message.blockName === 'string' && message.blockName.trim() !== '') {
      return blocks.findIndex((block: Block) => block.name === message.blockName);
    }
    return Number.isInteger(message.blockIndex) ? message.blockIndex! : -1;
  }, [blocks]);

  // Listen for block-focus messages from iframe (Full Roller only)
  useEffect(() => {
    if (effectivePreviewMode !== 'full') return;

    const handleMessage = (event: MessageEvent): void => {
      if (focusSyncSuppressedRef.current) return;
      if (event.source !== iframeRef.current?.contentWindow) return;

      const msg = event.data as PreviewFocusMessage | null;
      if (!msg || msg.type !== 'preview:block-focus') return;
      if (msg.reason !== 'scroll') return;

      const activeIframe = iframeRef.current;
      const visibleIndex = syncFocusToVisibleViewport && activeIframe
        ? getVisiblePreviewBlockIndex(activeIframe)
        : null;
      const resolvedIndex: number = visibleIndex ?? getMessageBlockIndex(msg);

      if (resolvedIndex >= 0 && resolvedIndex < blocks.length && resolvedIndex !== actualSelectedBlockIndex) {
        onPreviewFocusedBlock?.(resolvedIndex);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [actualSelectedBlockIndex, blocks.length, effectivePreviewMode, getMessageBlockIndex, getVisiblePreviewBlockIndex, onPreviewFocusedBlock, syncFocusToVisibleViewport]);

  // Scroll iframe to selected block when user clicks a tab (Full Roller only)
  useEffect(() => {
    if (effectivePreviewMode !== 'full' || !scrollToBlockCounter) return;
    if (scrollToBlockCounter === lastScrollToBlockCounterRef.current) return;
    lastScrollToBlockCounterRef.current = scrollToBlockCounter;

    const iframeWin: Window | null = iframeRef.current?.contentWindow ?? null;
    const iframeDoc: Document | null = iframeRef.current?.contentDocument ?? null;
    const activeIframe = iframeRef.current;
    if (!iframeWin || !iframeDoc || !activeIframe) return;

    const targetElement: HTMLElement | null = findPreviewBlockMarker(iframeDoc, {
      blockId: blocks[actualSelectedBlockIndex]?.id ?? null,
      blockIndex: actualSelectedBlockIndex,
    });
    if (!targetElement) return;

    suppressFocusSync();
    iframeWin.scrollTo({
      top: getCenteredPreviewMarkerScrollTop(iframeWin, activeIframe, targetElement),
      behavior: 'auto',
    });
  }, [actualSelectedBlockIndex, blocks, effectivePreviewMode, getCenteredPreviewMarkerScrollTop, scrollToBlockCounter, suppressFocusSync]);

  useEffect(() => {
    const iframeDoc: Document | null = iframeRef.current?.contentDocument ?? null;
    if (!iframeDoc) return;

    if (effectivePreviewMode !== 'full' && selectedLogoRowIndex === null) {
      clearSelectedPreviewBlock(iframeDoc);
      return;
    }

    activateSelectedPreviewBlock(iframeDoc, {
      blockId: selectedBlock?.id ?? null,
      blockIndex: actualSelectedBlockIndex,
    }, selectedLogoRowIndex);
  }, [actualSelectedBlockIndex, effectivePreviewMode, selectedBlock, selectedLogoRowIndex]);

  const handleIframeLoad = useCallback((
    event: React.SyntheticEvent<HTMLIFrameElement>,
    bufferIndex: PreviewBufferIndex,
    renderId: number
  ): void => {
    if (renderId !== pendingRenderIdRef.current) return;

    const iframe: HTMLIFrameElement = event.currentTarget;
    const iframeDoc: Document | null = iframe.contentDocument || iframe.contentWindow?.document || null;

    // Re-attach scroll listener (removes any previous handler first)
    const iframeWindow: Window | null = iframe.contentWindow;
    if (iframeWindow) {
      if (iframeScrollWindowRef.current && iframeScrollHandlerRef.current) {
        iframeScrollWindowRef.current.removeEventListener('scroll', iframeScrollHandlerRef.current);
      }
      const handler = (scrollEvent: Event) => {
        syncGridScroll(iframeWindow);
        const targetDoc = (scrollEvent.target as Document) ?? iframeWindow.document;
        savedIframeScrollRef.current =
          targetDoc?.documentElement?.scrollTop ||
          targetDoc?.body?.scrollTop ||
          iframeWindow.scrollY ||
          0;
      };
      iframeScrollHandlerRef.current = handler;
      iframeScrollWindowRef.current = iframeWindow;
      iframeWindow.addEventListener('scroll', handler, { passive: true });
      installCardNavigationListeners(iframeWindow);
    }

    try {
      if (iframeDoc?.body) {
        const contentHeight: number = iframeDoc.body.scrollHeight;
        setIframeHeight(Math.max(Math.min(contentHeight, originalContentHeight), originalContentHeight));
      }
    } catch {
      setIframeHeight(originalContentHeight);
    }

    requestAnimationFrame(() => {
      const container: HTMLDivElement | null = previewContainerRef.current;
      if (container) {
        if (fitToView) {
          savedScrollRef.current = 0;
          container.scrollTo({ top: 0 });
        } else {
          const maxScroll: number = container.scrollHeight - container.clientHeight;
          container.scrollTo({ top: Math.min(savedScrollRef.current, Math.max(0, maxScroll)) });
        }
      }
      const win: Window | null = iframe.contentWindow;
      if (win) {
        const currentIframeDoc: Document | null = iframe.contentDocument ?? win.document ?? null;
        const restoredFullPreviewSelection: boolean =
          effectivePreviewMode === 'full' && currentIframeDoc
            ? restoreSelectedBlockInFullPreview(win, currentIframeDoc, iframe)
            : false;

        if (!restoredFullPreviewSelection) {
          const docEl = win.document?.documentElement;
          const body = win.document?.body;
          const maxIframeScroll: number =
            Math.max(docEl?.scrollHeight ?? 0, body?.scrollHeight ?? 0) -
            iframe.clientHeight;
          win.scrollTo({ top: Math.min(savedIframeScrollRef.current, Math.max(0, maxIframeScroll)) });
        }
      }

      if (renderId === pendingRenderIdRef.current) {
        const loadedDocument: Document | null = iframe.contentDocument ?? win?.document ?? null;
        const currentPreviewState: PreviewStateRef = previewStateRef.current;
        if (loadedDocument) {
          if (currentPreviewState.effectivePreviewMode === 'full' || currentPreviewState.selectedLogoRowIndex !== null) {
            activateSelectedPreviewBlock(loadedDocument, {
              blockId: currentPreviewState.selectedBlock?.id ?? null,
              blockIndex: currentPreviewState.selectedBlockIndex,
            }, currentPreviewState.selectedLogoRowIndex);
          } else {
            clearSelectedPreviewBlock(loadedDocument);
          }
        }
        iframeRef.current = iframe;
        if (win) syncGridScroll(win);
        activeBufferIndexRef.current = bufferIndex;
        setActiveBufferIndex(bufferIndex);
      }
    });
  }, [effectivePreviewMode, fitToView, installCardNavigationListeners, originalContentHeight, restoreSelectedBlockInFullPreview, syncGridScroll]);

  const previewScopeSelectedIndex: number = effectivePreviewMode === 'full' && !selectedBlock?.isHidden
    ? -1
    : actualSelectedBlockIndex;

  const blocksForPreview: Block[] = useMemo((): Block[] => {
    return getBlocksForPreviewScope(
      blocks,
      cardGroups,
      previewScopeSelectedIndex,
      effectivePreviewMode,
      videoSettings.mode
    );
  }, [blocks, cardGroups, previewScopeSelectedIndex, effectivePreviewMode, videoSettings.mode]);

  useEffect(() => {
    const abortController = new AbortController();
    const requestId: number = requestIdRef.current + 1;
    requestIdRef.current = requestId;

    const generateHtml = async (): Promise<void> => {
      if (!blocksForPreview.length || !columnDefs.length) {
        resetPreviewState(null);
        return;
      }
      if (!selectedProjectId) {
        resetPreviewState('No project selected');
        return;
      }

      captureFullPreviewSelectionSnapshot();
      pendingRenderIdRef.current += 1;
      setLoading(true);
      setError(null);

      try {
        const apiBlocks: ApiBlock[] = blocksForPreview.map((block: Block): ApiBlock => (
          transformBlockToApiFormat(block, {
            columnDefs,
            propertiesMode: 'preview',
            includeId: true,
          })
        ));

        const payload: ApiPayload = {
          video_settings: {
            background_color: videoSettings.background_color,
            fps_num: videoSettings.fps_num,
            resolution: videoSettings.resolution,
            transparent: videoSettings.transparent,
            video_time: videoSettings.video_time,
            aspect_ratio: videoSettings.aspect_ratio,
            fonts: JSON.stringify(toFontUrlMap(videoSettings.fonts)),
            logos: JSON.stringify(videoSettings.logos),
            mode: videoSettings.mode,
            ...(videoSettings.mode === 'cards' ? { card_framing: videoSettings.card_framing } : {}),
          },
          data: apiBlocks,
        };

        const response = await authFetch(`${getApiUrl()}/html-preview?projectId=${selectedProjectId}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
          signal: abortController.signal,
        });

        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

        const result = await response.json();
        if (requestId !== requestIdRef.current) return;

        if (result.status === 'OK') {
          const nextHtml: string = result.html || '';
          if (nextHtml) {
            queuePreviewHtml(nextHtml);
          } else {
            resetPreviewState(null);
          }
        } else {
          console.error('Failed to generate HTML preview:', result.message);
          setError(result.message || 'Unknown error occurred');
        }
      } catch (err) {
        if (abortController.signal.aborted) return;
        if (requestId !== requestIdRef.current) return;
        console.error('Failed to generate HTML preview:', err);
        setError(err instanceof Error ? err.message : 'Failed to generate HTML preview');
      } finally {
        if (requestId === requestIdRef.current) {
          setLoading(false);
        }
      }
    };

    const scope = videoSettings.mode === 'cards' ? blocksForPreview.map(block => block.id).join('|') : 'full';
    const previousContext = previewRequestContextRef.current;
    const immediate = !previousContext || previousContext.projectId !== selectedProjectId || previousContext.mode !== videoSettings.mode
      || previousContext.scope !== scope || previousContext.retryCount !== retryCount || previousContext.columnDefs !== columnDefs
      || !blocksForPreview.length || !selectedProjectId;
    previewRequestContextRef.current = { projectId: selectedProjectId, mode: videoSettings.mode, scope, retryCount, columnDefs };
    const now = Date.now();
    if (immediate) previewBurstStartedRef.current = null;
    else previewBurstStartedRef.current ??= now;
    const delay = immediate ? 0 : Math.max(0, Math.min(200, 500 - (now - (previewBurstStartedRef.current ?? now))));
    const run = (): void => {
      previewBurstStartedRef.current = null;
      void generateHtml();
    };
    const timer = delay > 0 ? setTimeout(run, delay) : null;
    if (timer === null) run();
    return () => {
      if (timer !== null) clearTimeout(timer);
      abortController.abort();
    };
  }, [
    blocksForPreview,
    captureFullPreviewSelectionSnapshot,
    columnDefs,
    queuePreviewHtml,
    resetPreviewState,
    selectedProjectId,
    videoSettings,
    retryCount,
  ]);

  const previewWidth: number = videoDimensions.width;
  const baseFrameHeight: number = iframeHeight > 0 ? iframeHeight : originalContentHeight;
  const fitActive: boolean = fitToView;
  const effectiveScale: number = fitActive ? fitScale : zoom;
  // Roller is a scrolling document: use the entire pane instead of ending at one video frame.
  const frameHeight: number = fitActive && videoSettings.mode === 'roller' && effectiveScale > 0
    ? Math.max(baseFrameHeight, fitViewportHeight / effectiveScale)
    : baseFrameHeight;
  const gridSpacing: number = getPreviewGridSpacing(PREVIEW_GRID_CELL_SIZE, effectiveScale);
  const gridLineWidth: number = effectiveScale > 0 ? 1 / effectiveScale : 1;
  // Reveal intermediate lines only once they have at least 32 screen pixels of space.
  const subdivisionOpacity: number = 0.22 * Math.max(0, Math.min(1, (gridSpacing * effectiveScale - 64) / 16));
  const gridGradientStops: string = `rgba(128,128,128,0.22) ${gridLineWidth}px, transparent ${gridLineWidth}px, transparent ${gridSpacing / 2}px, rgba(128,128,128,${subdivisionOpacity}) ${gridSpacing / 2}px, rgba(128,128,128,${subdivisionOpacity}) ${gridSpacing / 2 + gridLineWidth}px, transparent ${gridSpacing / 2 + gridLineWidth}px`;
  const scaledPreviewWidth: number = previewWidth * effectiveScale;
  const scaledFrameHeight: number = frameHeight * effectiveScale;
  const activePreviewBuffer: PreviewBuffer = previewBuffers[activeBufferIndex];
  const activePreviewHtml: string = activePreviewBuffer.html;
  const activePreviewRenderId: number = activePreviewBuffer.renderId;
  const previewBufferEntries: Array<[PreviewBufferIndex, PreviewBuffer]> = useMemo(() => [
    [0, previewBuffers[0]],
    [1, previewBuffers[1]],
  ], [previewBuffers]);
  const renderedPreviewAvailable: boolean = previewBuffers.some((buffer) => Boolean(buffer.html));
  const visiblePreviewAvailable: boolean = Boolean(activePreviewHtml);

  const updateFitScale = useCallback((): void => {
    if (!fitActive) return;
    const container: HTMLDivElement | null = previewContainerRef.current;
    if (!container || previewWidth <= 0 || baseFrameHeight <= 0) { setFitScale(1); return; }
    const containerStyle: CSSStyleDeclaration = window.getComputedStyle(container);
    const horizontalPadding: number =
      parseFloat(containerStyle.paddingLeft || '0') + parseFloat(containerStyle.paddingRight || '0');
    const verticalPadding: number =
      parseFloat(containerStyle.paddingTop || '0') + parseFloat(containerStyle.paddingBottom || '0');
    const availableWidth: number = container.clientWidth - horizontalPadding;
    const availableHeight: number = container.clientHeight - verticalPadding;
    if (availableWidth <= 0 || availableHeight <= 0) { setFitScale(1); return; }
    const widthScale: number = availableWidth / previewWidth;
    const baseScale: number = Math.min(widthScale, availableHeight / baseFrameHeight, MAX_PREVIEW_ZOOM);
    setFitViewportHeight(availableHeight);
    setFitScale(baseScale);
  }, [fitActive, baseFrameHeight, previewWidth]);

  useEffect(() => {
    if (!fitActive) { setFitScale(1); return; }
    updateFitScale();
    const container: HTMLDivElement | null = previewContainerRef.current;
    const resizeObserver: ResizeObserver | null = 'ResizeObserver' in window
      ? new ResizeObserver(updateFitScale)
      : null;
    if (container && resizeObserver) {
      resizeObserver.observe(container);
    }
    window.addEventListener('resize', updateFitScale);
    return () => {
      resizeObserver?.disconnect();
      window.removeEventListener('resize', updateFitScale);
    };
  }, [activePreviewRenderId, fitActive, updateFitScale, iframeHeight]);

  useEffect(() => {
    if (!fitActive) return;
    savedScrollRef.current = 0;
    previewContainerRef.current?.scrollTo({ top: 0 });
  }, [fitActive]);

  const handleDownloadHtml = useCallback((): void => {
    if (!activePreviewHtml) return;

    const projectNameSegment: string = getSafeFileNameSegment(selectedProject?.name, 'credits');
    const scopeSegment: string = videoSettings.mode === 'cards' ? 'cards' : 'full';
    const fileName: string = `${projectNameSegment}_${scopeSegment}_preview.html`;
    const blob = new Blob([activePreviewHtml], { type: 'text/html;charset=utf-8' });
    const url: string = window.URL.createObjectURL(blob);
    const link: HTMLAnchorElement = document.createElement('a');

    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }, [activePreviewHtml, selectedProject?.name, videoSettings.mode]);

  return (
    <div id="html-preview" className="html-preview">
      {/* Controls stay compact so the preview remains the working surface. */}
      <div className="preview-controls">
        <div className="preview-controls-row">
          <div className="preview-control-group">
            <label className="fit-to-view-checkbox">
              <input
                type="checkbox"
                checked={fitToView}
                onChange={(e) => setFitToView(e.target.checked)}
              />
              <span>{t`Fit to view`}</span>
            </label>
            <label className="fit-to-view-checkbox">
              <input
                type="checkbox"
                checked={gridEnabled}
                onChange={(event) => {
                  setGridEnabled(event.target.checked);
                  localStorage.setItem('fintitles_previewGrid', String(event.target.checked));
                }}
              />
              <span>{t`Grid`}</span>
            </label>
             {!fitToView && <label className="zoom-control">
              <span>{t`Zoom:`}</span>
              <span className="zoom-slider-with-marker">
                <input
                  type="range"
                  min={MIN_PREVIEW_ZOOM}
                  max={MAX_PREVIEW_ZOOM}
                  step={0.01}
                  value={effectiveScale}
                  onChange={(e) => setZoom(snapPreviewZoom(parseFloat(e.target.value)))}
                />
                <span
                  className={`zoom-slider-default-marker${effectiveScale === DEFAULT_PREVIEW_ZOOM ? ' is-covered' : ''}`}
                  style={{ left: `${(DEFAULT_PREVIEW_ZOOM - MIN_PREVIEW_ZOOM) / (MAX_PREVIEW_ZOOM - MIN_PREVIEW_ZOOM) * 100}%` }}
                  aria-hidden="true"
                />
              </span>
              <span className="zoom-percentage">{Math.round(effectiveScale * 100)}%</span>
            </label>}
          </div>
          {isAdmin && (
            <button
              type="button"
              className="fancy-button preview-download-button"
              onClick={handleDownloadHtml}
              disabled={!activePreviewHtml || loading || Boolean(error)}
            >
              {t`Download`}
            </button>
          )}
        </div>
      </div>

      {(loading || renderedPreviewAvailable) && !visiblePreviewAvailable && !error && (
        <div className="preview-loading">
          <p>{t`Generating preview...`}</p>
        </div>
      )}

      {error && (
        <div className="preview-error">
          <p role="alert">{t`Could not update the preview.`} <button type="button" disabled={loading} onClick={() => setRetryCount(count => count + 1)}>{t`Retry`}</button></p>
        </div>
      )}

      {(!error || renderedPreviewAvailable) && (
        <div
          className="preview-container"
          style={{
            width: '100%',
            maxWidth: 'min(100%, calc(100vw - 48px))',
            overflowX: fitActive ? 'clip' : 'auto',
            overflowY: fitActive ? 'clip' : 'auto',
            position: 'relative',
            display: 'flex',
            justifyContent: 'flex-start',
            alignItems: 'flex-start',
            boxSizing: 'border-box',
            visibility: visiblePreviewAvailable ? 'visible' : 'hidden',
          }}
          ref={previewContainerRef}
          tabIndex={0}
          onWheel={(event) => handleCardWheel(event.deltaY, event)}
          onKeyDown={(event) => handleCardKeyDown(event.key, event)}
          onScroll={(e) => {
            if (!fitActive) {
              savedScrollRef.current = e.currentTarget.scrollTop;
            }
          }}
        >
          {renderedPreviewAvailable ? (
            <div
              style={{
                width: `${scaledPreviewWidth}px`,
                height: `${scaledFrameHeight}px`,
                flex: '0 0 auto',
                marginInline: 'auto',
                position: 'relative',
              }}
            >
              <div
                dir="ltr"
                style={{
                  transform: `scale(${effectiveScale})`,
                  transformOrigin: 'top left',
                  width: `${previewWidth}px`,
                  height: `${frameHeight}px`,
                  position: 'absolute',
                  top: 0,
                  left: 0,
                }}
              >
                {previewBufferEntries.map(([bufferIndex, buffer]) => {
                  if (!buffer.html) return null;

                  const isActiveBuffer: boolean = bufferIndex === activeBufferIndex;
                  const srcDoc: string = addPreviewScrollbarStyling(
                    addFitToViewScrollbarGutter(buffer.html)
                  );

                  return (
                    <iframe
                      dir="ltr"
                      key={`${bufferIndex}-${buffer.renderId}`}
                      style={{
                        width: `${previewWidth}px`,
                        height: `${frameHeight}px`,
                        border: 'none',
                        pointerEvents: isActiveBuffer ? 'auto' : 'none',
                        display: 'block',
                        visibility: isActiveBuffer ? 'visible' : 'hidden',
                        position: 'absolute',
                        inset: 0,
                        backgroundColor: videoSettings.transparent ? 'transparent' : videoSettings.background_color,
                      }}
                      srcDoc={srcDoc}
                      title={isActiveBuffer ? 'HTML Preview' : 'HTML Preview buffer'}
                      aria-hidden={!isActiveBuffer}
                      onLoad={(event) => handleIframeLoad(event, bufferIndex, buffer.renderId)}
                    />
                  );
                })}
                {gridEnabled && (
                  <div
                    ref={gridOverlayRef}
                    aria-hidden="true"
                    style={{
                      position: 'absolute',
                      inset: 0,
                      pointerEvents: 'none',
                      backgroundImage: `linear-gradient(to right, ${gridGradientStops}), linear-gradient(to bottom, ${gridGradientStops})`,
                      backgroundSize: `${gridSpacing}px ${gridSpacing}px`,
                    }}
                  />
                )}
              </div>
            </div>
          ) : (
            <p>{t`No preview available. Please select a block with data.`}</p>
          )}
        </div>
      )}
    </div>
  );
};

export default HtmlPreview;
